"use client";

import type { ClassAttributes, HTMLAttributes, ReactNode } from "react";
import { type Components, type ExtraProps } from "react-markdown";

import { MarketingVideo } from "@/components/marketing/marketing-video";

// react-markdown wraps a lone image in a <p>, so the inline video must use
// block-level spans (not divs) to stay valid phrasing content inside it.
const VIDEO_SRC_PATTERN = /\.(mp4|webm|ogg|mov)$/i;

// Type for heading component props from react-markdown
type HeadingProps = ClassAttributes<HTMLHeadingElement> &
  HTMLAttributes<HTMLHeadingElement> &
  ExtraProps & {
    children?: ReactNode;
  };

// Type for code component props from react-markdown
type CodeProps = ClassAttributes<HTMLElement> &
  HTMLAttributes<HTMLElement> &
  ExtraProps & {
    children?: ReactNode;
    inline?: boolean;
  };

export function generateHeadingId(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

export function getMarketingMarkdownComponents(options?: {
  withHeadingIds?: boolean;
}): Components {
  const withIds = options?.withHeadingIds === true;

  return {
    h1: ({ children, ...props }: HeadingProps) => {
      const commonProps = {
        className:
          "text-card-foreground mt-8 mb-6 scroll-mt-20 border-b pb-2 text-heading-32 text-balance",
        ...props,
      };
      if (withIds) {
        const text = String(children ?? "");
        const id = generateHeadingId(text);
        return (
          <h1 id={id} {...commonProps}>
            {children}
          </h1>
        );
      }
      return <h1 {...commonProps}>{children}</h1>;
    },
    h2: ({ children, ...props }: HeadingProps) => {
      const commonProps = {
        className:
          "mt-8 mb-4 scroll-mt-20 border-b pb-2 text-heading-24! text-balance text-foreground",
        ...props,
      };
      if (withIds) {
        const text = String(children ?? "");
        const id = generateHeadingId(text);
        return (
          <h2 id={id} {...commonProps}>
            {children}
          </h2>
        );
      }
      return <h2 {...commonProps}>{children}</h2>;
    },
    h3: ({ children, ...props }: HeadingProps) => {
      const commonProps = {
        className:
          "mt-6 mb-3 scroll-mt-20 text-heading-20! text-balance text-foreground",
        ...props,
      };
      if (withIds) {
        const text = String(children ?? "");
        const id = generateHeadingId(text);
        return (
          <h3 id={id} {...commonProps}>
            {children}
          </h3>
        );
      }
      return <h3 {...commonProps}>{children}</h3>;
    },
    h4: ({ children, ...props }: HeadingProps) => {
      const commonProps = {
        className:
          "text-card-foreground mt-5 mb-2 scroll-mt-20 text-heading-16 text-balance",
        ...props,
      };
      if (withIds) {
        const text = String(children ?? "");
        const id = generateHeadingId(text);
        return (
          <h4 id={id} {...commonProps}>
            {children}
          </h4>
        );
      }
      return <h4 {...commonProps}>{children}</h4>;
    },
    // Only attach IDs for h5/h6 when explicitly requested by consumers
    h5: withIds
      ? ({ children, ...props }: HeadingProps) => {
          const text = String(children ?? "");
          const id = generateHeadingId(text);
          return (
            <h5
              id={id}
              className="text-card-foreground text-heading-16 mt-4 mb-2 scroll-mt-20 text-balance"
              {...props}
            >
              {children}
            </h5>
          );
        }
      : undefined,
    h6: withIds
      ? ({ children, ...props }: HeadingProps) => {
          const text = String(children ?? "");
          const id = generateHeadingId(text);
          return (
            <h6
              id={id}
              className="text-card-foreground text-label-14 mt-4 mb-2 scroll-mt-20"
              {...props}
            >
              {children}
            </h6>
          );
        }
      : undefined,
    p: (props) => (
      <p className="text-card-foreground mb-4 not-first:mt-6" {...props} />
    ),
    a: (props) => (
      <a
        className="font-medium text-blue-500 no-underline hover:text-blue-600"
        {...props}
      />
    ),
    ul: (props) => (
      <ul className="my-6 ml-6 list-disc [&>li]:mt-2" {...props} />
    ),
    ol: (props) => (
      <ol className="my-6 ml-6 list-decimal [&>li]:mt-2" {...props} />
    ),
    li: (props) => <li className="text-card-foreground" {...props} />,
    img: ({ src, alt }) => {
      const url = typeof src === "string" ? src : "";
      if (VIDEO_SRC_PATTERN.test(url)) {
        return (
          // `not-prose`: typography gives <video> vertical margins, which push
          // the absolutely positioned player down and clip its controls.
          <span className="not-prose border-border bg-card my-6 block overflow-hidden rounded-lg border">
            <span className="relative block aspect-video w-full">
              <MarketingVideo
                src={url}
                title={alt || "Video"}
                className="absolute inset-0 h-full w-full object-contain"
              />
            </span>
          </span>
        );
      }
      return <img src={url} alt={alt ?? ""} />;
    },
    blockquote: (props) => (
      <blockquote
        className="text-muted-foreground mt-6 border-l-2 pl-6 italic"
        {...props}
      />
    ),
    code: ({ inline, className, children, ...props }: CodeProps) => {
      const match = /language-(\w+)/.exec(className || "");
      return !inline && match ? (
        <code
          className="bg-muted text-muted-foreground relative block w-full overflow-x-auto rounded p-4 font-mono text-sm"
          {...props}
        >
          {String(children).replace(/\n$/, "")}
        </code>
      ) : (
        <code
          className="bg-muted text-muted-foreground relative rounded px-[0.3rem] py-[0.2rem] font-mono text-sm"
          {...props}
        >
          {children}
        </code>
      );
    },
    pre: (props) => (
      <pre
        className="bg-muted my-6 overflow-x-auto rounded-lg border p-4"
        {...props}
      />
    ),
    table: (props) => (
      <div className="my-6 w-full overflow-y-auto">
        <table className="w-full" {...props} />
      </div>
    ),
    tr: (props) => <tr className="even:bg-muted m-0 border-t p-0" {...props} />,
    th: (props) => (
      <th
        className="border px-4 py-2 text-left [&[align=center]]:text-center [&[align=right]]:text-right"
        {...props}
      />
    ),
    td: (props) => (
      <td
        className="border px-4 py-2 text-left [&[align=center]]:text-center [&[align=right]]:text-right"
        {...props}
      />
    ),
  };
}
